/**
 * Account → Delete account (App Store + Google Play requirement: in-app account deletion).
 *
 * Destructive, owner-only flow. POST /api/account/delete (authenticated) permanently removes the
 * organization and ALL of its data. We require the user to re-enter their password AND type DELETE
 * to confirm before the button is enabled. On success session.deleteAccount() wipes the local
 * session (tokens + RevenueCat + push) and the AuthGate routes back to login / marketing.
 *
 * NEVER imports @smartbox/* (mobile invariant 1). Design-system primitives only; no raw hex/fonts.
 * Real backend round-trip via the typed client; loading + error states rendered (wrong password
 * surfaces the backend error message).
 */
import { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useSession } from '../../../../src/auth/session';
import { Screen, Text, Card, Button, Field } from '../../../../src/components/ui';
import { theme } from '../../../../src/theme';
import { extractErrorMessage } from '../../../../src/lib/format';

const CONFIRM_WORD = 'DELETE';

export default function DeleteAccountScreen() {
  const deleteAccount = useSession((s) => s.deleteAccount);

  const [password, setPassword] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirmed = confirmText.trim().toUpperCase() === CONFIRM_WORD && password.length > 0;
  const canSubmit = confirmed && !submitting;

  async function handleDelete() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      // On success the session clears and the AuthGate routes to /login (or marketing on web).
      // No navigation needed here. The gate reacts to status === 'unauthenticated'.
      await deleteAccount(password);
    } catch (e) {
      setError(extractErrorMessage(e, 'Could not delete your account. Please try again.'));
      setSubmitting(false);
    }
  }

  return (
    <Screen scroll>
      <Text variant="title" testID="delete-account-screen">Delete account</Text>

      <Card style={styles.warningCard}>
        <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
          This permanently deletes your organization and all of its data.
        </Text>
        <Text variant="body" color="secondary">
          Customers, leads, estimates, invoices, jobs, schedules, photos, documents, and every user
          on this organization will be removed. This cannot be undone and the data cannot be
          recovered.
        </Text>
      </Card>

      <Card>
        <Text variant="bodyStrong">This does not cancel your subscription</Text>
        <Text variant="body" color="secondary">
          This does not cancel your App Store or Google Play subscription. Cancel that separately in
          the store to stop billing.
        </Text>
      </Card>

      <Card>
        <Text variant="bodyStrong">Confirm it is you</Text>
        <Field
          label="Password"
          value={password}
          onChangeText={(t) => {
            setPassword(t);
            if (error) setError(null);
          }}
          secureToggle
          autoCapitalize="none"
          autoCorrect={false}
          textContentType="password"
          placeholder="Re-enter your password"
          testID="delete-account-password"
        />
        <Field
          label={`Type ${CONFIRM_WORD} to confirm`}
          value={confirmText}
          onChangeText={(t) => {
            setConfirmText(t);
            if (error) setError(null);
          }}
          autoCapitalize="characters"
          autoCorrect={false}
          placeholder={CONFIRM_WORD}
          testID="delete-account-confirm-input"
        />

        {error ? (
          <Text variant="small" color="danger" accessibilityRole="alert">
            {error}
          </Text>
        ) : null}

        <Button
          title="Delete account permanently"
          variant="danger"
          size="lg"
          onPress={() => void handleDelete()}
          disabled={!canSubmit}
          loading={submitting}
          testID="delete-account-confirm"
          accessibilityLabel="Delete account permanently"
        />
        {!confirmed ? (
          <Text variant="small" color="secondary">
            Enter your password and type {CONFIRM_WORD} to enable the button.
          </Text>
        ) : null}
      </Card>

      <View style={styles.footnote}>
        <Text variant="small" color="secondary">
          Prefer to keep your data but stop using Beam? You can cancel your subscription from Billing
          and plan instead.
        </Text>
      </View>
    </Screen>
  );
}

const styles = StyleSheet.create({
  warningCard: { borderColor: theme.color.action.danger },
  footnote: { marginTop: theme.spacing.sm },
});
